import { useEffect, useState, lazy, Suspense } from "react";
import { Navigate } from "react-router-dom";
import { LogOut, User, Compass, Eye, CreditCard, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
const Roadmap = lazy(() => import("@/pages/Roadmap"));
const Portfolio = lazy(() => import("@/pages/Portfolio"));
const SubscriptionPanel = lazy(() => import("@/components/SubscriptionPanel"));
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useBilling } from "@/context/BillingContext";
import { UpgradeGate } from "@/components/UpgradeGate";

const TabFallback = () => <div className="min-h-[40vh] flex items-center justify-center font-mono-data text-sm text-muted-foreground animate-pulse" data-testid="tab-loading">Loading…</div>;

const NotifDot = ({ testid }) => (
  <span data-testid={testid} className="ml-2 relative flex h-2 w-2">
    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent opacity-75" />
    <span className="relative inline-flex rounded-full h-2 w-2 bg-accent" />
  </span>
);

export default function Dashboard() {
  const { user, loading, logout } = useAuth();
  const { ent } = useBilling();
  const isMember = !!ent?.premium;
  const [dealNew, setDealNew] = useState(false);
  const [fresh, setFresh] = useState(null);

  useEffect(() => {
    if (!user) return;
    api.get("/stats/freshness?days=3").then((r) => setFresh(r.data)).catch(() => {});
  }, [user]);

  useEffect(() => {
    if (!user || !ent?.premium) return;
    api.get("/deals/today").then((r) => {
      const d = r.data || {};
      const sig = `${d.date}|${d.property?.id || ""}|${d.vehicle?.id || ""}`;
      window.__deal_sig = sig;
      setDealNew(localStorage.getItem("luxora_seen_deal") !== sig);
    }).catch(() => {});
  }, [user]);

  const markDealSeen = () => {
    if (window.__deal_sig) localStorage.setItem("luxora_seen_deal", window.__deal_sig);
    setDealNew(false);
    window.dispatchEvent(new Event("luxora-deal-seen"));
  };

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center font-mono-data text-sm text-muted-foreground animate-pulse">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;

  return (
    <div className="max-w-[1600px] mx-auto px-5 sm:px-8 py-12">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <div>
          <h1 className="font-display text-4xl tracking-tighter font-medium">Your Account</h1>
          <p className="text-muted-foreground mt-1">Welcome back, {user.name}</p>
          {fresh && fresh.total > 0 && (
            <div data-testid="corpus-freshness-badge" className="mt-3 flex flex-wrap gap-2">
              {[["homes", "Homes"], ["vehicles", "Vehicles"]].map(([k, label]) => fresh[k]?.total > 0 && (
                <div
                  key={k}
                  data-testid={`freshness-badge-${k}`}
                  title={`${fresh[k].fresh.toLocaleString()} of ${fresh[k].total.toLocaleString()} ${label.toLowerCase()} re-confirmed against source in the last ${fresh.days} days`}
                  className="inline-flex items-center gap-2 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-3 py-1.5 text-[11px] font-mono-data uppercase tracking-wider text-emerald-400">
                  <ShieldCheck className="w-3.5 h-3.5" strokeWidth={2} />
                  {label}: <span data-testid={`freshness-pct-${k}`}>{fresh[k].verified_pct}%</span> verified in the last {fresh.days} days
                </div>
              ))}
            </div>
          )}
        </div>
        <Button variant="outline" onClick={logout} data-testid="logout-button" className="rounded-full"><LogOut className="w-4 h-4 mr-2" />Sign out</Button>
      </div>

      <Tabs defaultValue="account" onValueChange={(v) => { if (v === "watchlist") markDealSeen(); }}>
        <TabsList data-testid="account-tabs" className="grid grid-cols-2 gap-1 h-auto w-full sm:inline-flex sm:w-auto sm:flex-wrap [&>button]:w-full sm:[&>button]:w-auto [&>button]:text-xs sm:[&>button]:text-sm">
          <TabsTrigger value="account" data-testid="account-tab-overview"><User className="w-4 h-4 mr-2" />Overview</TabsTrigger>
          <TabsTrigger value="roadmap" data-testid="account-tab-roadmap"><Compass className="w-4 h-4 mr-2" />Road Map</TabsTrigger>
          <TabsTrigger value="watchlist" data-testid="account-tab-watchlist"><Eye className="w-4 h-4 mr-2" />Deals / Watchlist{dealNew && <NotifDot testid="watchlist-notif-dot" />}</TabsTrigger>
          <TabsTrigger value="subscription" data-testid="account-tab-subscription"><CreditCard className="w-4 h-4 mr-2" />Subscription</TabsTrigger>
        </TabsList>

        <TabsContent value="account" className="mt-8">
          <div className="grid gap-5 sm:grid-cols-2">
            <div className="bg-surface border border-border rounded-xl p-6" data-testid="account-profile-card">
              <div className="flex items-center gap-2 mb-2"><User className="w-4 h-4 text-accent" /><span className="font-display font-medium">Profile</span></div>
              <p className="text-sm text-muted-foreground">Signed in as <span className="text-foreground">{user.name}</span></p>
              {user.email && <p className="text-sm text-muted-foreground mt-0.5 break-all">{user.email}</p>}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="roadmap" className="mt-8" data-testid="account-roadmap-panel">
          {isMember
            ? <div className="-mx-5 sm:-mx-8 -mt-8"><Suspense fallback={<TabFallback />}><Roadmap embedded /></Suspense></div>
            : <UpgradeGate title="Wealth Road Map" note="The Road Map planner is included with the Vaelith Membership. Join to model your path to any luxury home or vehicle." />}
        </TabsContent>

        <TabsContent value="watchlist" className="mt-8" data-testid="account-watchlist-panel">
          {isMember
            ? <div className="-mx-5 sm:-mx-8 -mt-8"><Suspense fallback={<TabFallback />}><Portfolio embedded /></Suspense></div>
            : <UpgradeGate title="Deals & Watchlist" note="Saved deals, your watchlist and price-drop alerts are included with the Vaelith Membership." />}
        </TabsContent>

        <TabsContent value="subscription" className="mt-8" data-testid="account-subscription-panel">
          <Suspense fallback={<TabFallback />}><SubscriptionPanel /></Suspense>
        </TabsContent>
      </Tabs>
    </div>
  );
}
